(() => {
  'use strict';

  if (window.AoCartV4) return;

  const ROOT_SELECTOR = '[data-ao-cart-v4]';
  const STATE_SELECTOR = '[data-ao-cart-v4-state]';
  const REOPEN_KEY = 'ao-cart-v4:reopen-after-discount';
  const PROTECTION_ROLE = 'shipping_protection';
  const observedRoots = new WeakMap();
  const reconciliationSignatures = new Set();

  let pendingProtection = null;
  let protectionRequestInFlight = false;
  let scanQueued = false;
  let lastShippingState = null;
  let drawerWasOpen = false;
  let rewardIntroTimer = 0;
  let checkoutResetTimer = 0;
  let historyRefreshRequest = null;

  function currentRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function currentStateNode(root = currentRoot()) {
    if (!root) return null;
    return root.querySelector(STATE_SELECTOR) || root;
  }

  function numberFrom(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function readState(root = currentRoot()) {
    const node = currentStateNode(root);
    if (!root || !node) return null;

    return {
      root,
      node,
      sectionId: node.dataset.sectionId || root.dataset.sectionId || 'cart-drawer',
      visibleCount: Math.max(0, numberFrom(node.dataset.visibleCount || root.dataset.visibleCount)),
      protectionVariantId: node.dataset.protectionVariantId || root.dataset.protectionVariantId || '',
      protectionLineKey: node.dataset.protectionLineKey || root.dataset.protectionLineKey || '',
      protectionQuantity: Math.max(0, numberFrom(node.dataset.protectionQuantity || root.dataset.protectionQuantity)),
      hasShippable: (node.dataset.hasShippable || root.dataset.hasShippable) === 'true',
      itemSingular: root.dataset.itemSingular || 'item',
      itemPlural: root.dataset.itemPlural || 'items',
    };
  }

  function readShippingState(root = currentRoot()) {
    const node = root?.querySelector('[data-ao-cart-v4-shipping]');
    if (!node) return null;

    return {
      node,
      mode: node.dataset.mode || 'threshold',
      currency: node.dataset.currency || '',
      thresholdCents: Math.max(0, numberFrom(node.dataset.thresholdCents)),
      qualifyingSubtotalCents: Math.max(0, numberFrom(node.dataset.qualifyingSubtotalCents)),
      remainingCents: Math.max(0, numberFrom(node.dataset.remainingCents)),
      progressPercent: Math.min(100, Math.max(0, numberFrom(node.dataset.progressPercent))),
      unlocked: node.dataset.unlocked === 'true',
      animationEnabled: node.dataset.animationEnabled !== 'false',
    };
  }

  function syncShipping(root) {
    const shipping = readShippingState(root);
    if (!shipping) {
      lastShippingState = null;
      return null;
    }

    const previous = lastShippingState;
    const samePolicy = previous
      && previous.mode === shipping.mode
      && previous.currency === shipping.currency
      && previous.thresholdCents === shipping.thresholdCents;
    const motionAllowed = shipping.animationEnabled
      && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const fill = shipping.node.querySelector('[data-ao-cart-v4-shipping-fill]');

    if (
      samePolicy
      && motionAllowed
      && fill
      && Math.abs(previous.progressPercent - shipping.progressPercent) > 0.01
    ) {
      fill.style.transition = 'none';
      fill.style.width = `${previous.progressPercent}%`;
      fill.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        if (!fill.isConnected) return;
        fill.style.removeProperty('transition');
        fill.style.width = `${shipping.progressPercent}%`;
      });
    }

    if (samePolicy && motionAllowed && !previous.unlocked && shipping.unlocked) {
      shipping.node.classList.remove('is-just-relocked');
      shipping.node.classList.add('is-just-unlocked');
      window.setTimeout(() => shipping.node.classList.remove('is-just-unlocked'), 1100);
    }

    if (samePolicy && motionAllowed && previous.unlocked && !shipping.unlocked) {
      shipping.node.classList.remove('is-just-unlocked');
      shipping.node.classList.add('is-just-relocked');
      window.setTimeout(() => shipping.node.classList.remove('is-just-relocked'), 480);
    }

    lastShippingState = {
      mode: shipping.mode,
      currency: shipping.currency,
      thresholdCents: shipping.thresholdCents,
      qualifyingSubtotalCents: shipping.qualifyingSubtotalCents,
      remainingCents: shipping.remainingCents,
      progressPercent: shipping.progressPercent,
      unlocked: shipping.unlocked,
      animationEnabled: shipping.animationEnabled,
    };
    return shipping;
  }

  function clearRewardIntro(root = currentRoot()) {
    if (rewardIntroTimer) {
      window.clearTimeout(rewardIntroTimer);
      rewardIntroTimer = 0;
    }
    root?.querySelectorAll('.is-reward-intro').forEach((node) => node.classList.remove('is-reward-intro'));
  }

  function syncRewardIntro(root, shipping) {
    const isOpen = root?.classList.contains('active');
    if (!isOpen) {
      drawerWasOpen = false;
      clearRewardIntro(root);
      return;
    }
    if (!shipping || drawerWasOpen) return;

    drawerWasOpen = true;
    const motionAllowed = shipping.animationEnabled
      && !shipping.unlocked
      && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!motionAllowed) return;

    const targets = [
      shipping.node,
      root.querySelector('[data-ao-cart-v4-shipping-unlocker="true"]'),
    ].filter(Boolean);

    targets.forEach((node) => {
      node.classList.remove('is-reward-intro');
      node.getBoundingClientRect();
      node.classList.add('is-reward-intro');
    });

    const fill = shipping.node.querySelector('[data-ao-cart-v4-shipping-fill]');
    if (fill && shipping.progressPercent > 0) {
      fill.style.transition = 'none';
      fill.style.width = '0%';
      fill.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        if (!fill.isConnected) return;
        fill.style.removeProperty('transition');
        fill.style.width = `${shipping.progressPercent}%`;
      });
    }

    rewardIntroTimer = window.setTimeout(() => {
      targets.forEach((node) => node.classList.remove('is-reward-intro'));
      rewardIntroTimer = 0;
    }, 1040);
  }

  function syncHeaderCount(state) {
    if (!state) return;
    const count = state.visibleCount;

    document
      .querySelectorAll('[data-aof-cart-count], [data-aocdx-cart-count], [data-ao-cart-count]')
      .forEach((node) => {
        if (node.textContent !== String(count)) node.textContent = String(count);
      });

    document
      .querySelectorAll('[data-aof-cart-label], [data-aocdx-cart-label], [data-ao-cart-label]')
      .forEach((node) => {
        if (!node.dataset.aoCartBaseLabel) {
          const currentLabel = node.getAttribute('aria-label') || 'Cart';
          node.dataset.aoCartBaseLabel = currentLabel.replace(/,\s*\d+.*$/, '').trim() || 'Cart';
        }
        const itemLabel = count === 1 ? state.itemSingular : state.itemPlural;
        /* the visible label reads "Cart · N", so the accessible name must contain that exact text */
        node.removeAttribute('aria-label');
      });

    state.root.classList.toggle('is-empty', count === 0);
  }

  function protectionStatus(root, message) {
    const status = root?.querySelector('[data-aocdx-protection-status]');
    if (status) status.textContent = window.aoLocalizedStatus?.[message] || message;
  }

  function clearPendingProtection(message = '') {
    const root = currentRoot();
    const toggle = root?.querySelector('[data-aocdx-protection-switch]');
    const host = toggle?.closest('[data-aocdx-protection="true"]');

    if (toggle) {
      toggle.disabled = false;
      toggle.removeAttribute('aria-disabled');
    }
    host?.removeAttribute('aria-busy');
    if (message) protectionStatus(root, message);
    pendingProtection = null;
  }

  function syncProtection(state) {
    if (!state) return;
    const selected = Boolean(state.protectionLineKey && state.protectionQuantity > 0);
    const toggle = state.root.querySelector('[data-aocdx-protection-switch]');
    const host = toggle?.closest('[data-aocdx-protection="true"]');

    if (host) host.dataset.selected = String(selected);
    if (toggle) {
      toggle.setAttribute('aria-checked', String(selected));
      toggle.disabled = false;
      toggle.removeAttribute('aria-disabled');
    }

    if (!pendingProtection) return;
    if (pendingProtection.desired !== selected) return;

    const message = selected ? 'Protection added' : 'Protection removed';
    protectionStatus(state.root, message);
    host?.removeAttribute('aria-busy');
    pendingProtection = null;

    window.requestAnimationFrame(() => {
      state.root.querySelector('[data-aocdx-protection-switch]')?.focus({ preventScroll: true });
    });
  }

  function sectionIds(state) {
    const cartItems = state.root.querySelector('cart-drawer-items');
    const source = cartItems && typeof cartItems.getSectionsToRender === 'function'
      ? cartItems
      : state.root;

    if (source && typeof source.getSectionsToRender === 'function') {
      try {
        const ids = source
          .getSectionsToRender()
          .map((entry) => entry.section || entry.id)
          .filter(Boolean);
        if (ids.length) return [...new Set(ids)];
      } catch (_error) {
        // The stable Shopify section fallbacks below preserve cart rendering.
      }
    }

    return [state.sectionId || 'cart-drawer', 'cart-icon-bubble'];
  }

  async function correctProtectionQuantity(state, quantity, signature) {
    protectionRequestInFlight = true;
    reconciliationSignatures.add(signature);

    try {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${routeRoot}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: state.protectionLineKey,
          quantity,
          sections: sectionIds(state),
          sections_url: window.location.pathname,
        }),
      });

      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || 'Protection could not be corrected.');

      const drawer = currentRoot();
      if (drawer && cart.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(cart, true);
      }

      document.dispatchEvent(new CustomEvent('aocdx:cart-updated', { detail: { cart } }));
    } catch (_error) {
      reconciliationSignatures.delete(signature);
      clearPendingProtection('Unable to update. Please try again.');
    } finally {
      protectionRequestInFlight = false;
    }
  }

  function reconcileProtection(state) {
    if (!state || protectionRequestInFlight || !state.protectionLineKey) return;

    let targetQuantity = null;
    if (!state.hasShippable) targetQuantity = 0;
    else if (state.protectionQuantity !== 1) targetQuantity = 1;
    if (targetQuantity === null) return;

    const signature = `${state.protectionLineKey}:${state.protectionQuantity}>${targetQuantity}`;
    if (reconciliationSignatures.has(signature)) return;
    correctProtectionQuantity(state, targetQuantity, signature);
  }

  function processRoot(root) {
    const state = readState(root);
    if (!state) return;
    syncHeaderCount(state);
    const shipping = syncShipping(root);
    syncRewardIntro(root, shipping);
    syncProtection(state);
    reconcileProtection(state);

    document.dispatchEvent(new CustomEvent('ao-cart-v4:rendered', {
      detail: {
        visibleCount: state.visibleCount,
        hasProtection: state.protectionQuantity > 0,
        shipping: shipping ? {
          mode: shipping.mode,
          currency: shipping.currency,
          thresholdCents: shipping.thresholdCents,
          qualifyingSubtotalCents: shipping.qualifyingSubtotalCents,
          remainingCents: shipping.remainingCents,
          progressPercent: shipping.progressPercent,
          unlocked: shipping.unlocked,
          animationEnabled: shipping.animationEnabled,
        } : null,
      },
    }));
  }

  function scheduleScan(root = currentRoot()) {
    if (!root || scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(() => {
      scanQueued = false;
      processRoot(root.isConnected ? root : currentRoot());
    });
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) {
      scheduleScan(root);
      return;
    }

    const observer = new MutationObserver((records) => {
      const hasRelevantReplacement = records.some((record) => (
        Array.from(record.addedNodes).some((node) => (
          node.nodeType === Node.ELEMENT_NODE
          && (node.matches?.(STATE_SELECTOR) || node.querySelector?.(STATE_SELECTOR))
        ))
      ));
      const drawerStateChanged = records.some((record) => (
        record.type === 'attributes'
        && record.target === root
        && record.attributeName === 'class'
      ));
      if (hasRelevantReplacement || drawerStateChanged) scheduleScan(root);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    observedRoots.set(root, observer);
    processRoot(root);
  }

  function scan(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) observeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(observeRoot);
  }

  function rememberRitualCode(event) {
    const form = event.target.closest?.('[data-ao-cart-v4-code-form]');
    if (!form) return;
    const input = form.querySelector('[name="cart-discount-field"]');
    const code = input?.value.trim() || '';
    if (!code) return;

    try {
      window.sessionStorage.setItem(REOPEN_KEY, String(Date.now()));
    } catch (_error) {
      // Discount application still works when storage is unavailable.
    }
  }

  function resetCheckoutState() {
    if (checkoutResetTimer) {
      window.clearTimeout(checkoutResetTimer);
      checkoutResetTimer = 0;
    }

    document.querySelectorAll(`${ROOT_SELECTOR} .cart__checkout-button`).forEach((button) => {
      const root = button.closest(ROOT_SELECTOR);
      const state = readState(root);
      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-loading');
      button.disabled = !state || state.visibleCount === 0;
      button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    });
  }

  function checkoutLoad(event) {
    const clickedBtn = event?.currentTarget
      || event?.target?.closest?.('.cart__checkout-button');
    if (!clickedBtn) return;

    const drawer = clickedBtn.closest('cart-drawer');
    const tnc = drawer?.querySelector('[id^="TNC-Checkbox-"]');
    if (tnc && tnc.dataset.checked !== 'true') return;

    clickedBtn.classList.add('loading');
    clickedBtn.setAttribute('aria-busy', 'true');
    clickedBtn.dataset.loading = 'true';
    const spinner = clickedBtn.querySelector('.loading-overlay__spinner');
    spinner?.classList.remove('hidden');

    if (checkoutResetTimer) window.clearTimeout(checkoutResetTimer);
    checkoutResetTimer = window.setTimeout(() => {
      checkoutResetTimer = 0;
      if (document.location.pathname.includes('/checkout')) return;
      resetCheckoutState();
    }, 8000);
  }

  function cancelHistoryRefresh() {
    historyRefreshRequest?.abort();
    historyRefreshRequest = null;
  }

  async function refreshHistoryCart() {
    const state = readState();
    const root = state?.root;
    if (!root || typeof root.renderContents !== 'function') return;

    cancelHistoryRefresh();
    const controller = new AbortController();
    historyRefreshRequest = controller;
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    const ids = sectionIds(state);
    // Keep the current locale, market and Draft preview context. This GET never
    // changes cart lines; Shopify returns their authoritative rendered state.
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) return;
      const sections = await response.json();
      if (controller.signal.aborted || currentRoot() !== root
        || currentStateNode(root) !== state.node || protectionRequestInFlight) return;
      // A successful HTTP response can still contain null section failures.
      if (ids.some((id) => typeof sections?.[id] !== 'string' || !sections[id].trim())) return;

      const wasOpen = root.classList.contains('active');
      root.renderContents({ sections }, true);
      if (wasOpen && !root.classList.contains('active')) root.open?.();
      else if (!wasOpen && root.classList.contains('active')) root.close?.();
      syncHeaderCount(readState(root));
      resetCheckoutState();
      scan(document);
    } catch (_error) {
      // Keep the existing UI on a failed read; never fake an empty cart or retry
      // an add. A later normal navigation will obtain server-rendered state.
    } finally {
      window.clearTimeout(timeout);
      if (historyRefreshRequest === controller) historyRefreshRequest = null;
    }
  }

  function recoverFromHistory(event) {
    resetCheckoutState();
    window.requestAnimationFrame(() => {
      resetCheckoutState();
      scan(document);
    });
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (event?.persisted || navigation?.type === 'back_forward') refreshHistoryCart();
  }

  function cancelHistoryReadForMutation(event) {
    if (!historyRefreshRequest) return;
    const selector = event.type === 'submit'
      ? 'form[action*="/cart"], form[data-ao-home-add-form]'
      : 'cart-remove-button, .quantity__button, .quantity__input, .upsell-toggle-btn, .upsell__plus-btn, [data-aocdx-protection-switch], [data-ao-home-add-submit], [data-ao-home-add-proxy], .cart__checkout-button';
    if (event.target?.closest?.(selector)) cancelHistoryRefresh();
  }

  // Cart V4 owns the protection request. The role travels only in this request's line
  // properties; it is never rendered as a named properties[...] input that another
  // product form could collect.
  async function requestProtection(state, desired, host) {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const sections = sectionIds(state);
    let url = '';
    let payload = null;

    if (desired) {
      const variantId = Number(state.protectionVariantId || host?.dataset.id);
      const roleNode = host?.querySelector('[data-ao-cart-role]');
      const role = roleNode?.dataset.aoCartRole || PROTECTION_ROLE;
      if (Number.isFinite(variantId) && variantId > 0) {
        url = `${routeRoot}cart/add.js`;
        payload = {
          items: [{ id: variantId, quantity: 1, properties: { _ao_cart_role: role } }],
          sections,
          sections_url: window.location.pathname,
        };
      }
    } else if (state.protectionLineKey) {
      url = `${routeRoot}cart/change.js`;
      payload = {
        id: state.protectionLineKey,
        quantity: 0,
        sections,
        sections_url: window.location.pathname,
      };
    }

    if (!payload) {
      clearPendingProtection('Unable to update. Please try again.');
      scheduleScan();
      return;
    }

    protectionRequestInFlight = true;
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Protection could not be updated.');

      const drawer = currentRoot();
      if (drawer && result.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(result, true);
      }

      if (!desired) document.dispatchEvent(new CustomEvent('aocdx:cart-updated', { detail: { cart: result } }));
    } catch (_error) {
      clearPendingProtection('Unable to update. Please try again.');
      scheduleScan();
    } finally {
      protectionRequestInFlight = false;
    }
  }

  function handleProtectionToggle(event) {
    const toggle = event.target.closest?.('[data-aocdx-protection-switch]');
    const root = currentRoot();
    if (!toggle || !root?.contains(toggle)) return;

    // Keep the generic upsell toggle from submitting its own product form for this line.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pendingProtection || protectionRequestInFlight || toggle.disabled) return;

    const state = readState(root);
    if (!state) return;

    const selected = Boolean(state.protectionLineKey && state.protectionQuantity > 0);
    const intent = { desired: !selected, startedAt: Date.now() };
    pendingProtection = intent;
    const host = toggle.closest('[data-aocdx-protection="true"]');
    host?.setAttribute('aria-busy', 'true');
    toggle.disabled = true;
    toggle.setAttribute('aria-disabled', 'true');
    protectionStatus(root, '');

    window.setTimeout(() => {
      if (pendingProtection !== intent) return;
      clearPendingProtection('Unable to update. Please try again.');
    }, 8000);

    requestProtection(state, intent.desired, host);
  }

  function reopenAfterDiscount() {
    let timestamp = 0;
    try {
      timestamp = numberFrom(window.sessionStorage.getItem(REOPEN_KEY));
    } catch (_error) {
      return;
    }

    if (!timestamp) return;
    window.sessionStorage.removeItem(REOPEN_KEY);
    if (Date.now() - timestamp > 120000) return;

    const drawer = currentRoot();
    if (!drawer || typeof drawer.open !== 'function') return;
    window.setTimeout(() => drawer.open(), 80);
  }

  function init() {
    resetCheckoutState();
    scan(document);
    reopenAfterDiscount();
  }

  document.addEventListener('submit', rememberRitualCode, true);
  document.addEventListener('click', handleProtectionToggle, true);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target?.querySelectorAll) return;
    const roots = [...event.target.querySelectorAll(ROOT_SELECTOR)];
    if (event.target.matches?.(ROOT_SELECTOR)) roots.push(event.target);
    roots.forEach((root) => {
      observedRoots.get(root)?.disconnect();
      observedRoots.delete(root);
    });
  });
  window.addEventListener('pageshow', recoverFromHistory);
  window.addEventListener('pagehide', cancelHistoryRefresh);
  document.addEventListener('submit', cancelHistoryReadForMutation, true);
  document.addEventListener('click', cancelHistoryReadForMutation, true);
  document.addEventListener('change', cancelHistoryReadForMutation, true);

  window.checkoutLoad = checkoutLoad;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.AoCartV4 = {
    checkoutLoad,
    resetCheckoutState,
    readState,
    readShippingState,
    refresh: () => scan(document),
  };
})();
